import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Chip, Radio, Tooltip } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatOfflineScheduleLabel } from '../../../../../Common/Util/Schedule';

/**
 * 옵션 변경 후보 1행.
 *
 * 차단 사유 문구(reasonText)는 서버 판정 함수가 내려준 값을 **그대로** 출력한다.
 * 어드민에서 문구를 다시 만들지 않는다 (판정과 문구를 서버 한 곳에서만 관리).
 */
function CandidateRow(props) {
  const { candidate, selected, onSelect } = props;
  const intl = useIntl();

  const changeable = candidate.changeable !== false;

  const occupied = (candidate.student_count || 0) + (candidate.student_ready_count || 0);
  const unlimited = !candidate.student_limit;
  const isFull = !unlimited && occupied >= candidate.student_limit;

  const priceDiff = useMemo(() => {
    if (typeof candidate.price_diff === 'number') {
      return candidate.price_diff;
    }
    return 0;
  }, [candidate.price_diff]);

  const priceDiffText = useMemo(() => {
    if (priceDiff === 0) {
      return '차이 없음';
    }
    if (priceDiff > 0) {
      return `+${priceDiff.toLocaleString()}원 (추가 차액 — 청구되지 않음)`;
    }
    return `${priceDiff.toLocaleString()}원 (감액 차액 — 환불되지 않음)`;
  }, [priceDiff]);

  // 라벨은 서버가 만들어 내려준다(이력 스냅샷과 동일 포맷). 화면에서 class_dates 를 다시 조합하지 않는다.
  const label = candidate.label || formatOfflineScheduleLabel(candidate, intl);

  return (
    <tr
      className={
        'option-change-modal__candidate' +
        (changeable ? '' : ' option-change-modal__candidate--blocked') +
        (selected ? ' option-change-modal__candidate--selected' : '')
      }
      onClick={() => changeable && onSelect(candidate.id)}
    >
      <td className="option-change-modal__candidate-radio">
        <Radio
          checked={!!selected}
          disabled={!changeable}
          value={candidate.id}
          size="small"
          inputProps={{ 'aria-label': `옵션 ${candidate.id} 선택` }}
          onChange={() => onSelect(candidate.id)}
        />
      </td>
      <td className="option-change-modal__candidate-option">
        <div className="option-change-modal__candidate-title">
          {label}
          {!changeable && <BlockIcon fontSize="small" className="option-change-modal__blocked-icon" />}
        </div>
        {candidate.class_location && (
          <div className="option-change-modal__candidate-sub">{candidate.class_location}</div>
        )}
        {!changeable && candidate.reasonText && (
          <div className="option-change-modal__blocked-reason">
            <WarningAmberIcon fontSize="small" />
            <span>{candidate.reasonText}</span>
          </div>
        )}
      </td>
      <td className="option-change-modal__candidate-period">
        {candidate.start_date}
        <br />~{candidate.end_date}
      </td>
      <td className="option-change-modal__candidate-capacity">
        {unlimited ? (
          '제한 없음'
        ) : (
          <Tooltip
            title={`결제완료 ${candidate.student_count || 0} · 결제대기 ${candidate.student_ready_count || 0}`}
            arrow
          >
            <span>
              {occupied}/{candidate.student_limit}
              {isFull && <Chip label="마감" size="small" color="error" className="option-change-modal__full-chip" />}
            </span>
          </Tooltip>
        )}
      </td>
      <td
        className={
          'option-change-modal__candidate-price' +
          (priceDiff !== 0 ? ' option-change-modal__candidate-price--warn' : '')
        }
      >
        {priceDiffText}
      </td>
    </tr>
  );
}

export default CandidateRow;
